
import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from "react-intl";

import CustomTable from 'components/table';
import {  ActionMenu, TableViewButton, TableFlagButton, CustomButton, ConfirmationButton } from 'components';
import { masterPaginationServices, tableInitialState, } from 'utils/constants';
import { getTableParams } from 'utils/global';
import { RootReducerState } from 'utils/types';
import { getMasterPaginationData } from 'redux/actions';
import PageLayout from 'layouts/PageLayout';
import { userColumns } from 'utils/constants/tableColumn/column';
import { services } from 'redux/services';
import { useToastMessage } from 'hooks';
import View from './View';
import { Delete, RemoveRedEye, Watch } from '@mui/icons-material';

const Agent: React.FC = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { formatMessage } = useIntl();
    const { toastMessage } = useToastMessage();
    const dispatch = useDispatch();

    const [tableState, setTableState] = useState(tableInitialState);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [selectedData, setSelectedData] = useState(null);

    const { userData, loading } = useSelector(
        ({ masterPaginationReducer }: RootReducerState) => ({
            userData: masterPaginationReducer[masterPaginationServices.user].data,
            loading: masterPaginationReducer[masterPaginationServices.user].loading,
        }),
        shallowEqual
    );

    const { items, count } = userData;

    useEffect(() => {
        onApiCall();
    }, [tableState]);

    function onApiCall() {
        const params = getTableParams(tableState);
        dispatch(getMasterPaginationData(masterPaginationServices.user, params));
    }

    let columnAction = {
        label: formatMessage({ id: "action" }),
        name: "",
        options: {
            customBodyRender: (_: any, tableMeta: any) => {
                const rowData = items[tableMeta.rowIndex];
                return (
                    <ActionMenu>
                        <CustomButton
                            color='success'
                            size='small'
                            label="View"
                            variant='outlined'
                            onClick={() => handleRowClick(rowData)}
                            startIcon={<RemoveRedEye />}
                        />
                        {!rowData?.role.includes('admin') &&
                            <ConfirmationButton
                                color='error'
                                size='small'
                                label="Delete"
                                variant='outlined'
                                onConfirm={() => onDeleteData(rowData)}
                                title={formatMessage({ id: "delete-title" })}
                                subTitle={formatMessage({ id: "delete-confirmation" })}
                                startIcon={<Delete />}
                            />
                        }
                    </ActionMenu>
                )
            }
        }
    }

    function handleRowClick(rowData: any) {
        setViewModal(true);
        setSelectedData(rowData);
    }

    function onDeleteData(data: any) {
        setDeleteLoading(true);
        services.deleteUser({ uuid: data.uuid })
            .then((res: any) => {
                onApiCall();
                toastMessage(formatMessage({ id: "deleted-message" }, { title: formatMessage({ id: 'user' }) }));
            })
            .catch((err) => {
                toastMessage(JSON.stringify(err?.data?.message) || formatMessage({ id: "opps-error-message" }), 'error');
            })
            .finally(() => setDeleteLoading(false))
    }

    return (
        <PageLayout title={formatMessage({ id: "user" })} hideToolbar>
            <CustomTable
                columns={[...userColumns(formatMessage), columnAction]}
                tableData={items}
                tableState={tableState}
                itemsCount={count}
                setTableState={setTableState}
                loading={loading || deleteLoading}
            />
            {viewModal && selectedData && 
                <View
                    open={viewModal}
                    onClose={() => setViewModal(false)}
                    selectedData={selectedData}
                />
            }
        </PageLayout>
    )
}

export default Agent;
